import React from 'react';
import PropTypes from 'prop-types';

// On-brand sleep-depth ramp (awake → deep), aligned to the au palette
// (coral accent → teal → navy) so the panel reads as one product with the rest.
export const STAGE_CONFIG = {
  SLEEP_AWAKE:  { label: 'Awake',  color: '#f2a154' }, // warm amber (lightest)
  SLEEP_REM:    { label: 'REM',    color: '#6c8ed6' }, // soft blue
  SLEEP_LIGHT:  { label: 'Light',  color: '#52b5a8' }, // light teal
  SLEEP_DEEP:   { label: 'Deep',   color: '#2f5c8f' }, // navy (deepest)
  SLEEP_ASLEEP: { label: 'Asleep', color: '#2e9b87' }, // brand teal
};

export const STAGE_ORDER = ['SLEEP_AWAKE', 'SLEEP_REM', 'SLEEP_LIGHT', 'SLEEP_DEEP', 'SLEEP_ASLEEP'];

const formatMinutes = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} m`;
  return `${h} h ${m} m`;
};

const formatDuration = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h} Hour ${m} Min`;
};

const formatEpochToTime = (epochSeconds, epochMillis) => {
  if (!epochSeconds && !epochMillis) return '';
  const fromDate = epochSeconds ? new Date(parseInt(epochSeconds) * 1000) : null;
  const toDate = epochMillis ? new Date(parseInt(epochMillis)) : null;

  const fmt = (d) => {
    if (!d) return '';
    let hours = d.getUTCHours();
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const fromStr = fmt(fromDate);
  const toStr = fmt(toDate);
  if (fromStr && toStr) return `${fromStr} - ${toStr}`;
  return fromStr || toStr;
};

const SleepStageBreakdown = ({ breakdown }) => {
  if (!breakdown || !breakdown.stages || breakdown.stages.length === 0) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No stage data for this date</div>;
  }

  const { stages, total_minutes, time_from, time_to, uuid_count } = breakdown;

  // Sort stages by defined order
  const sortedStages = [...stages].sort((a, b) => {
    const ai = STAGE_ORDER.indexOf(a.data_type);
    const bi = STAGE_ORDER.indexOf(b.data_type);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  return (
    <div data-testid="sleep-stage-breakdown">
      {/* Whole-night summary meta */}
      <div className="ss-stage-meta">
        <span style={{ fontSize: '20px' }}>&#x1F319;</span>
        <div>
          <div className="ss-stage-meta__dur">{formatDuration(total_minutes)}</div>
          <div className="ss-stage-meta__sub">{formatEpochToTime(time_from, time_to)}</div>
        </div>
        {uuid_count > 0 && (
          <div className="ss-stage-meta__sub" style={{ marginLeft: 'auto' }}>
            {uuid_count} source{uuid_count > 1 ? 's' : ''} contributed
          </div>
        )}
      </div>

      {/* Per-stage % bars — every value text + value (never colour alone) */}
      <div className="ss-stage-list">
        {sortedStages.map((stage) => {
          const config = STAGE_CONFIG[stage.data_type] || { label: stage.data_type, color: '#94a3b8' };
          return (
            <div className="ss-stage-row" key={stage.data_type} data-testid={`ss-stage-${stage.data_type}`}>
              <span className="ss-stage-pct" style={{ background: config.color }}>
                {Math.round(stage.percentage)}%
              </span>
              <span className="ss-stage-track">
                <span style={{ width: `${Math.max(stage.percentage, 2)}%`, background: config.color }} />
              </span>
              <span className="ss-stage-name">{config.label} {formatMinutes(stage.total_minutes)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

SleepStageBreakdown.propTypes = {
  breakdown: PropTypes.shape({
    stages: PropTypes.arrayOf(PropTypes.shape({
      data_type: PropTypes.string.isRequired,
      total_minutes: PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired,
    })),
    total_minutes: PropTypes.number,
    time_from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    time_to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uuid_count: PropTypes.number,
  }),
};

export default SleepStageBreakdown;
